import {
  Button,
  Card,
  FormattedMessageDiv,
  GreenCheck,
  LoadingCard,
  Navigation,
  OnDeckLogo,
  PlaidLogo,
  useWizardContext,
} from '@odmonorepo/core-components';
import { useApplication } from 'merchant-api/unified';
import { getCheckoutState } from 'merchant-api/checkout';
import {
  checkInstitutionSupport,
  getSalesAgentInfo,
  usePlaid,
} from 'merchant-api/spiceroad';
import { decide, PLAID_TRUE_MANDATORY_FLAG } from 'merchant-api/vendor/optimizely';
import { useAnalytics } from 'use-analytics';
import { useState, useCallback, useEffect } from 'react';
import { Alert, Stack } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import {
  PlaidLinkOnSuccess,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOnExit,
  PlaidLinkError,
  PlaidLinkOnExitMetadata,
  PlaidLinkOptions,
  usePlaidLink,
} from 'react-plaid-link';
import { checkoutSteps } from '../../CheckoutWizard';
import { atom, useAtom } from 'jotai';
import { Application } from 'merchant-api/types';

export const notNowClicked = atom(false)

export function PlaidLauncherCard() {
  const { nextStep, goToStep } = useWizardContext<typeof checkoutSteps>();
  const { applicationData, isLoadingApplication, isErrorApplication } =
    useApplication();
  const {
    getClientInfo,
    trackPlaid,
    isLoading,
    isPlaidConnected,
    plaidPartnerInfo,
    isLoadingPlaidMerchantStatus
  } = usePlaid(true);

  const { checkoutData, isLoadingCheckoutState, isErrorCheckoutState } =
    getCheckoutState();
  const { salesAgentInfo } = getSalesAgentInfo();
  const { isInstitutionSupported, isLoadingInstitutionSupport } = checkInstitutionSupport();

  const [errorAlert, setErrorAlert] = useState(false);
  const [linkToken, setLinkToken] = useState('');
  const [plaidSuccess, setPlaidSuccess] = useState(false);
  const [skipPlaid, setSkipPlaid] = useState(false);
  const [notNow, setNotNow] = useAtom(notNowClicked);
  const [preClosedApplicationData, setPreClosedApplicationData] = useState<Application>(applicationData!);
  const analytics = useAnalytics();

  useEffect(() => {
    // since checkout is completing as we render this component, useApplication() eventually returns null data
    // storing applicationData when it is defined to get around this
    if (applicationData != null) {
      setPreClosedApplicationData(applicationData);
    }
  }, [applicationData]);

  useEffect(() => {
    if (isLoadingInstitutionSupport || isInstitutionSupported == null) return;
    !isInstitutionSupported ? nextStep() : null;
  }, [isInstitutionSupported, isLoadingInstitutionSupport]);


  useEffect(() => {
    if (isLoadingPlaidMerchantStatus || isPlaidConnected == null) return;
    isPlaidConnected ? nextStep() : null;
  }, [isPlaidConnected, isLoadingPlaidMerchantStatus]);

  useEffect(() => {
    (async () => {
      try {
        const isDirectChannel =
          preClosedApplicationData?.metadata?.channelCode === 'DIRECT';
        const storageKey = isDirectChannel ? 'salesAgentEmail' : 'partnerId';
        let identifier = sessionStorage.getItem(storageKey);
        if (!identifier) {
          const fetchData = isDirectChannel ? salesAgentInfo : await plaidPartnerInfo;
          if (fetchData !== null) {
            identifier = isDirectChannel ? fetchData?.email : fetchData?.partnerId;
          }
          else {
            identifier = null;
          }
          !!identifier && sessionStorage.setItem(storageKey, identifier);
        }

        if (identifier === undefined) {
          return;
        }

        const data = await decide(PLAID_TRUE_MANDATORY_FLAG, {
          identifier: identifier,
          ondeck_salesChannel: preClosedApplicationData?.metadata?.channelCode,
          customerType: preClosedApplicationData?.type,
        }, analytics);
        setSkipPlaid(data !== 'skip_plaid_disabled');
      } catch (error) {
        return null;
      }
    })();
  }, [preClosedApplicationData, salesAgentInfo, plaidPartnerInfo]);

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    async (public_token: string, metadata: PlaidLinkOnSuccessMetadata) => {
      setErrorAlert(false);
      trackPlaid({
        callback: 'onSuccess',
        metadata: JSON.stringify(metadata),
        publicToken: public_token,
        eventName: 'OPEN',
        error: '',
        variation: skipPlaid ? 'skip_enabled' : 'skip_disabled',
      })
        .then(nextStep())
        .catch(() => {
          setErrorAlert(true);
        });
    },
    [],
  );

  const onExit = useCallback<PlaidLinkOnExit>(
    async (error: null | PlaidLinkError, metadata: PlaidLinkOnExitMetadata) => {
      setLinkToken('');
      if (error) {
        setErrorAlert(true);
        if (
          error.error_code === 'INSUFFICIENT_CREDENTIALS' ||
          error.error_code === 'INVALID_CREDENTIALS'
        ) {
          if (sessionStorage.getItem('attempted') || false) {
            setSkipPlaid(true);
          } else {
            sessionStorage.setItem('attempted', 'true');
          }
        } else {
          setSkipPlaid(true);
        }
      }
      await trackPlaid({
        callback: 'onExit',
        metadata: JSON.stringify(metadata),
        publicToken: '',
        eventName: 'EXIT',
        error: error ? JSON.stringify(error) : '',
        variation: skipPlaid ? 'skip_enabled' : 'skip_disabled',
      });
    },
    [],
  );
  const config: PlaidLinkOptions = { onSuccess, onExit, token: linkToken };

  const { open, ready } = usePlaidLink(config);

  const launchPlaid = async () => {
    setErrorAlert(false);
    const clientInfo = await getClientInfo();
    clientInfo?.link_token
      ? setLinkToken(clientInfo?.link_token)
      : setErrorAlert(true);
  };

  useEffect(() => {
    document.body.style.overflow = 'auto';
    !plaidSuccess && !errorAlert && ready && open();
  }, [ready, launchPlaid, errorAlert, plaidSuccess]);

  if (isErrorApplication || isErrorCheckoutState)
    throw new Error('Unable to load necessary data');
  if (isLoadingApplication || isLoadingCheckoutState || isLoadingPlaidMerchantStatus || isLoadingInstitutionSupport) return <LoadingCard />;

  return (
    <Stack
      gap={6}
      style={{ maxWidth: '600px', width: '100%' }}
      className="mx-auto"
    >
      <FormattedMessageDiv
        className="h3 text-center"
        id="plaid.launcher.title"
      />
      {errorAlert && (
        <Alert variant="danger">
          <FormattedMessageDiv
            className="fw-bold"
            id="plaid.launcher.errorAlert"
          />
          { salesAgentInfo?.name ? (
            <FormattedMessageDiv
            className='fs-8'
              id="plaid.launcher.errorAlert.subtext.salesAgent"
              values={{
                agent: (
                  <>
                    {salesAgentInfo?.name} at <strong>{salesAgentInfo?.phone}</strong>
                  </>
                ),
              }}
            />
          ) : (
            <FormattedMessageDiv
              className="fs-8"
              id="plaid.launcher.errorAlert.subtext.customerService"
              values={{
                tel: (txt: any) => <strong>{txt}</strong>,
              }}
            />
          )}
        </Alert>
      )}
      <Card
        style={{ maxWidth: '600px', margin: '0 auto' }}
        bodyClassName="p-md-6 px-md-8 p-5"
      >
        <Stack gap={6}>
          <div className="d-flex flex-wrap justify-content-center align-items-center gap-4 mb-4">
            <OnDeckLogo />
            <PlaidLogo />
          </div>
          <FormattedMessageDiv id="plaid.launcher.description" />
          <Stack direction="horizontal" gap={4} className="align-items-start">
            <GreenCheck />
            <FormattedMessageDiv
              id="plaid.launcher.check1"
              values={{
                title: (
                  <strong>
                    <FormattedMessage id="plaid.launcher.check1.title" />
                  </strong>
                ),
              }}
            />
          </Stack>
          <Stack direction="horizontal" gap={4} className="align-items-start">
            <GreenCheck />
            <FormattedMessageDiv
              id="plaid.launcher.check2"
              values={{
                title: (
                  <strong>
                    <FormattedMessage id="plaid.launcher.check2.title" />
                  </strong>
                ),
              }}
            />
          </Stack>
          <Stack direction="horizontal" gap={4} className="align-items-start">
            <GreenCheck />
            <FormattedMessageDiv
              id="plaid.launcher.check3"
              values={{
                title: (
                  <strong>
                    <FormattedMessage id="plaid.launcher.check3.title" />
                  </strong>
                ),
              }}
            />
          </Stack>
          <FormattedMessageDiv
            className="fw-semibold"
            id="plaid.launcher.bottomText"
          />
        </Stack>
      </Card>
      <Navigation
        validate={false}
        continueOnly={!skipPlaid}
        handleNext={() => launchPlaid()}
        isLoading={isLoading}
        continueText={<FormattedMessage id="plaid.launcher.connectBank" />}
        customBackBtn={
          <Button
            variant="outline-primary"
            style={{ width: '100%' }}
            onClick={() => { setNotNow(true); checkoutData?.checkoutStatus === 'COMPLETED' ? goToStep('CHECKOUT_SUCCESS') : nextStep()}}
          >
            <FormattedMessage id="plaid.launcher.notNow" />
          </Button>
        }
      />
    </Stack>
  );
}

export default PlaidLauncherCard;
